import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

const STORAGE_KEY = 'pageIntroCollapsed';

interface IPageIntroContextValue {
  collapsed: boolean;
  toggle: () => void;
  /** True when at least one PageIntro is mounted on the current page. */
  hasIntro: boolean;
  /** Mount-side registration — call from PageIntro's effect, returns cleanup. */
  registerIntro: () => () => void;
}

const PageIntroContext = createContext<IPageIntroContextValue | null>(null);

const readStored = (): boolean => {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    // Private mode / disabled storage — fall back to expanded.
    return false;
  }
};

export const PageIntroProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [collapsed, setCollapsed] = useState<boolean>(readStored);
  // Counter, not boolean — a single page could mount multiple PageIntros and
  // we want the toggle visible until the last one unregisters.
  const [registeredCount, setRegisteredCount] = useState(0);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, collapsed ? '1' : '0');
    } catch {
      // ignore quota / privacy mode
    }
  }, [collapsed]);

  const toggle = useCallback(() => setCollapsed((prev) => !prev), []);

  const registerIntro = useCallback(() => {
    setRegisteredCount((prev) => prev + 1);
    return () => setRegisteredCount((prev) => Math.max(0, prev - 1));
  }, []);

  const value = useMemo<IPageIntroContextValue>(
    () => ({
      collapsed,
      toggle,
      hasIntro: registeredCount > 0,
      registerIntro,
    }),
    [collapsed, toggle, registeredCount, registerIntro],
  );

  return (
    <PageIntroContext.Provider value={value}>
      {children}
    </PageIntroContext.Provider>
  );
};

/**
 * Reads the shared page-intro state. Returns a no-op fallback when used
 * outside a provider so legacy pages don't crash.
 */
export const usePageIntro = (): IPageIntroContextValue => {
  const ctx = useContext(PageIntroContext);
  if (ctx) return ctx;
  return {
    collapsed: false,
    toggle: () => {},
    hasIntro: false,
    registerIntro: () => () => {},
  };
};
